import React, { useState } from 'react';
import PropTypes from 'prop-types';
import css from './WaitingRoomLink.scss';
import Icon from 'SF/MaterialIcon';
import { getVirtualKioskLink } from 'utils/kiosk';
import classnames from 'classnames';
import { SFEntityID } from 'utils/types';
import { isEmptyString } from 'utils/text';
import VirtualKioskClosedModal from './VirtualKioskClosedModal';
import StyledButton from "./StyledButton";

/**
 * Generates a button that has a link to the virtual kiosk room, or in the case that the kiosk is not open, a modal with
 * information about why it is closed.
 *
 * @param {Object} props - see propTypes below
 * @returns {JSX.Element|null} - the button including link text indicating the status and functionality to redirect to
 * the kiosk or open the informational modal.
 */
function WaitingRoomLink(props) {
    // Do not show the link for non-students or when functionality disabled
    if (!props.isStudent || !props.virtualCheckInEnabled) return null;

    const {checkedIntoService, className, kioskId, serviceId, serviceName, virtualCheckInOpen} = props;
    const [displayWaitingRoomClosedDialog, setDisplayWaitingRoomClosedDialog] = useState(false);

    const labelText = () => checkedIntoService ?
        (virtualCheckInOpen ? "Check Out from" : "Check Out unavailable for")
        : (virtualCheckInOpen ? "Join Waiting Room for" : "Waiting Room closed for")

    const linkText = () => checkedIntoService ?
        (virtualCheckInOpen ? "Check Out" : "Check Out Unavailable")
        : (virtualCheckInOpen ? "Join Waiting Room" : "Waiting Room Closed")

    const linkFunction = () => virtualCheckInOpen ?
        location.assign(getVirtualKioskLink(kioskId, serviceId)) : setDisplayWaitingRoomClosedDialog(true)

    return (
        <div className={classnames(css['card-header-link'], {[className]: !isEmptyString(className)})}>
            <VirtualKioskClosedModal
                checkedIntoService={checkedIntoService}
                setDisplayClosed={() => setDisplayWaitingRoomClosedDialog(false)}
                shouldDisplay={displayWaitingRoomClosedDialog} />
            {checkedIntoService && <Icon className={css['checkout-icon']} aria-hidden={true} icon="assignment_return" />}
            <StyledButton onClick={linkFunction}
                          asLink={true}
                          className={classnames({[css.disabled]: !virtualCheckInOpen})}
                          aria-label={`${labelText()} ${serviceName}`}
                          aria-disabled={!virtualCheckInOpen}>
                {linkText().toUpperCase()}
            </StyledButton>
        </div>
    );
}

WaitingRoomLink.propTypes = {
    // Whether the current user is already checked into the service for this link
    checkedIntoService: PropTypes.bool,
    // Any classes to be added for styling the button
    className: PropTypes.string,
    // Whether the current user is a student
    isStudent: PropTypes.bool,
    // Identifier for the kiosk associated with this link
    kioskId: PropTypes.number,
    // Identifier for the service associated with this link
    serviceId: SFEntityID.isRequired,
    // Name for the service associated with this link
    serviceName: PropTypes.string.isRequired,
    // Whether this kiosk has virtual check-in functionality enabled
    virtualCheckInEnabled: PropTypes.bool,
    // Whether this kiosk is currently available for virtual check-in
    virtualCheckInOpen: PropTypes.bool
}

export default WaitingRoomLink;
